import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useDataTypesFromMetricIds } from '../../hooks/useDataTypesFromSeriesAndMetricListItems';
import DatasetDefinitionsContext from '../../contexts/DatasetDefinitionsContext';
import useGetFieldLabel from '../../hooks/useGetFieldLabel';

// Field types that can be grouped by an interval (day / week / month / …).
const isDateFieldType = (type: FleetOps.FieldType) => type === 'date';

// Field types that make sense as a grouping dimension.
const isGroupableFieldType = (type: FleetOps.FieldType) =>
  type === 'text' || isDateFieldType(type);

const useRankingMatrixGroupByField = (
  metrics: VisualisationDefinitions.RankingMatrixMetric[],
  initialField?: string,
) => {
  const { datasets } = useContext(DatasetDefinitionsContext);
  const [selectedMetricIds, setSelectedMetricIds] = useState<string[]>([]);
  const dataTypes = useDataTypesFromMetricIds(selectedMetricIds);
  const { getFieldLabel } = useGetFieldLabel();

  const [groupByField, setGroupByField] = useState<string | undefined>(
    initialField,
  );
  const [groupingOptions, setGroupingOptions] = useState<DropdownOption[]>([]);

  useEffect(() => {
    setSelectedMetricIds(metrics.map((m) => m.metricId));
  }, [metrics]);

  // Fields (across the selected metrics' datasets) that are date-typed. Used to
  // decide whether the interval selector should be shown for the current
  // grouping field.
  const dateFields = useMemo(() => {
    const fields = new Set<string>();
    dataTypes.forEach((dt) => {
      const set = datasets.find((s) => s.type === dt);
      set?.fields.forEach((f) => {
        if (isDateFieldType(f.type)) {
          fields.add(f.field);
        }
      });
    });
    return fields;
  }, [dataTypes, datasets]);

  useEffect(() => {
    const options = [] as DropdownOption[];
    dataTypes.forEach((dt) => {
      const set = datasets.find((s) => s.type === dt);
      if (set) {
        set.fields.forEach((f) => {
          if (isGroupableFieldType(f.type)) {
            options.push({
              label: getFieldLabel({ field: f.field, dataType: f.type }),
              onSelected: () => {
                setGroupByField(f.field);
              },
            });
          }
        });
      }
    });

    setGroupingOptions(_.sortBy(options, 'label'));
  }, [dataTypes, datasets, getFieldLabel]);

  const clearGrouping = useCallback(() => {
    setGroupByField(undefined);
  }, []);

  const isDateGrouping = !!groupByField && dateFields.has(groupByField);

  return {
    groupByField,
    groupingOptions,
    clearGrouping,
    isDateGrouping,
  };
};

export default useRankingMatrixGroupByField;
